/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect, ReactElement } from "react";

import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import * as CountryRegionSelectors from "Dataflows/CountryRegion/CountryRegionSelector";
import * as IndustryFocusSelectors from "Dataflows/IndustryFocus/IndustryFocusSelector";
import * as RdUserProfilesActions from "Dataflows/RD/UserProfile/RDUserProfileAction";
import * as RdUserProfileSelectors from "Dataflows/RD/UserProfile/RDUserProfileSelector";
import * as SocialNetworkSelectors from "Dataflows/SocialNetwork/SocialNetworkSelector";
import * as StateProvinceSelectors from "Dataflows/StateProvince/StateProvinceSelector";
import * as TechnicalExpertiseSelectors from "Dataflows/TechnicalExpertise/TechnicalExpertiseSelector";

import { useAuth } from "Authentication/AuthProvider";
import { IRegionalDirectorDetailsProps } from "Components/RegionalDirectorDetails/IRegionalDirectorDetailsProps";
import { RegionalDirectorDetails } from "Components/RegionalDirectorDetails/RegionalDirectorDetails";
import { OperationStatus } from "Constants/OperationStatus";
import { PermissionsEnum, RolesEnum } from "Constants/RolesPermissionsConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";

/**
 * The Regional Director Details Container.
 * @returns {ReactElement} The react element.
 */
const RegionalDirectorsDetailsContainer = (): ReactElement => {
  const dispatch = useDispatch();
  const { account, hasRole, hasPermission } = useAuth();
  const createdOrModifiedBy = account.userName;
  const { id } = useParams<{ id: string }>();
  const userProfileId = Number.parseInt(id);
  const selectedUserProfile = useSelector(RdUserProfileSelectors.getSelectedUserProfile);

  const isCurrentUserProfileLoading = useSelector(
    RdUserProfileSelectors.getSelectedUserProfileIsLoadingStatus
  );
  const isUserProfileCommentsLoading = useSelector(
    RdUserProfileSelectors.getUserProfileCommentsIsLoadingStatus
  );
  const selectedUserProfileErrorMessage = useSelector(
    RdUserProfileSelectors.getFetchUserProfileErrorMessage
  );
  const userProfileCommentsErrorMessage = useSelector(
    RdUserProfileSelectors.getUserProfileCommentsErrorMessage
  );
  const userProfileComments = useSelector(RdUserProfileSelectors.getUserProfileComments);
  const addUserProfileCommentStatus = useSelector(
    RdUserProfileSelectors.getUserProfileAddCommentInProgressStatus
  );
  const updateProgramProfileStatus = useSelector(
    RdUserProfileSelectors.getProgramProfileUpdateStatus
  );

  const state = useSelector(RdUserProfileSelectors.getState);

  const countryRegionOptionList = CountryRegionSelectors.getCountryRegionsOptionList(
    state.countryRegionDataState
  );
  const countryRegionPhoneOptionList = CountryRegionSelectors.getCountryRegionPhoneOptionList(
    state.countryRegionDataState
  );
  const industryFocusOptionList = IndustryFocusSelectors.getIndustryFocusOptionList(
    state.industryFocusDataState
  );
  const stateProvinceOptionList = StateProvinceSelectors.getStateProvincesOptionList(
    state.stateProvinceDataState
  );
  const stateProvinceShippingOptionList = StateProvinceSelectors.getstateProvinceShippingOptionList(
    state.stateProvinceDataState
  );
  const socialNetworkOptionList = SocialNetworkSelectors.getSocialNetworkOptionList(
    state.socialNetworkDataState
  );
  const technicalExpertiseOptionList = TechnicalExpertiseSelectors.getTechnicalExpertiseOptionList(
    state.technicalExpertiseDataState
  );
  const userProfileIsPrivateUpdateStatus = useSelector(
    RdUserProfileSelectors.getUserProfileUpdateIsPrivateOperationStatus
  );
  const updatePrimaryEmailStatus = useSelector(
    RdUserProfileSelectors.getUserProfileUpdatePrimaryEmailStatus
  );
  const updatePrimaryEmailError = useSelector(
    RdUserProfileSelectors.getUserProfileUpdatePrimaryEmailError
  );

  const canUpdatePrimaryEmail =
    (hasRole(RolesEnum.RD_PROGRAM_OWNER) || hasRole(RolesEnum.RD_BENEFITS_MANAGER)) &&
    hasPermission(TenantTypesEnum.RD, PermissionsEnum.RD_UPDATE_PRIMARY_EMAIL);

  useEffect(() => {
    if (userProfileId) {
      dispatch(RdUserProfilesActions.updateSelectedUserProfileId(userProfileId));
      dispatch(RdUserProfilesActions.fetchUserProfile(userProfileId));
    }
  }, [userProfileId]);

  useEffect(() => {
    if (userProfileIsPrivateUpdateStatus === OperationStatus.Succeeded) {
      dispatch(RdUserProfilesActions.fetchUserProfile(userProfileId));
    }
  }, [userProfileIsPrivateUpdateStatus]);

  const detailsProps: IRegionalDirectorDetailsProps = {
    isCurrentUserProfileLoading,
    isUserProfileCommentsLoading,
    selectedUserProfile,
    userProfileId: userProfileId || 0,
    selectedUserProfileErrorMessage,
    userProfileCommentsErrorMessage,
    userProfileComments,
    addUserProfileCommentStatus,
    updateProgramProfileStatus,
    countryRegionOptionList,
    countryRegionPhoneOptionList,
    industryFocusOptionList,
    stateProvinceOptionList,
    stateProvinceShippingOptionList,
    socialNetworkOptionList,
    technicalExpertiseOptionList,
    canUpdatePrimaryEmail,
    updatePrimaryEmailStatus,
    updatePrimaryEmailError,
    onAddCommentClicked: (textValue) => {
      if (!!userProfileId) {
        dispatch(
          RdUserProfilesActions.addUserProfileComment(userProfileId, textValue, createdOrModifiedBy)
        );
      }
    },
    fetchUserProfileComments: (userProfileId) =>
      dispatch(RdUserProfilesActions.fetchUserProfileComments(userProfileId)),
    onClaimBenefit: (benefitType: string) => {
      if (!!userProfileId) {
        dispatch(RdUserProfilesActions.claimRdBenefit(benefitType, userProfileId));
      }
    },
    onDismissClaimBenefitPanel: () => {
      dispatch(RdUserProfilesActions.claimRdBenefitReset());
    },
    onPrimaryEmailUpdate: (userProfileId: number, newEmail: string) => {
      dispatch(RdUserProfilesActions.userProfilePrimaryEmailUpdate(userProfileId, newEmail));
    },
    onPrimaryEmailUpdateReset: () => {
      dispatch(RdUserProfilesActions.userProfilePrimaryEmailUpdateReset());
    },
    loadUserProfile: (userProfileId: number) => {
      dispatch(RdUserProfilesActions.fetchUserProfile(userProfileId));
    },
  };

  return <RegionalDirectorDetails {...detailsProps} />;
};

export default RegionalDirectorsDetailsContainer;
